import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Box,
  Checkbox,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  ListItemText,
  OutlinedInput,
  Chip,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import BusinessIcon from "@mui/icons-material/Business";
import Warehouse from "@mui/icons-material/Warehouse";
import DownloadIcon from "@mui/icons-material/Download";
import GridTable from "../../components/GridTable";
import TextField from "@mui/material/TextField";
import ActionMenu from "../../components/ActionMenu";
import Dialog from "../../components/Dialog";
import Loader from "../../components/Loader";
import Button from "../../components/Button";
import { successMessage, errorMessage } from "../../components/Toaster";
import {
  getAllCompanies,
  updateCompany,
  deleteCompany,
  createCompany,
  downloadCompanyScanImages,
} from "./api";
import { getYardsByCompanyId } from "../Company/Yard/api";
import { getUsers } from "../Company/Yard/User/api";
import { getErrorMessage } from "../../utils/error";

const TODAY = new Date().toISOString().split("T")[0];

const fieldLabelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: "#6B7280",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "6px",
  display: "block",
};

const inputBaseStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  fontSize: 14,
  color: "#111827",
  background: "#F9FAFB",
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  outline: "none",
  fontFamily: "inherit",
  transition: "border-color 0.15s",
};

const inputErrorStyle = {
  borderColor: "#E24B4A",
  background: "#FEF2F2",
};

const Company = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editCompany, setEditCompany] = useState(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [loading, setLoading] = useState({
    update: false,
    delete: false,
    create: false,
    fetch: false,
    download: false,
    yards: false,
    users: false,
  });

  // ── Download modal state ──────────────────────────────────────────────
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadCompany, setDownloadCompany] = useState(null);
  const [downloadDates, setDownloadDates] = useState({
    startDate: TODAY,
    endDate: TODAY,
  });
  const [downloadDateErrors, setDownloadDateErrors] = useState({});
  const [yards, setYards] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedYardIds, setSelectedYardIds] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState(null);

  const allYardsSelected = selectedYardIds === null;
  const noYardsSelected =
    selectedYardIds !== null && selectedYardIds.length === 0;
  const showUserDropdown = !noYardsSelected;

  // ── Validation ───────────────────────────────────────────────────────
  const validationSchema = Yup.object().shape({
    companyName: Yup.string().required("Company name is required"),
    displayName: Yup.string().required("Display name is required"),
    companyLogo: Yup.string()
      .url("Company logo must be a valid URL")
      .nullable(),
    contactNo: Yup.string()
      .matches(/^\d{10}$/, "Phone Number must be exactly 10 digits")
      .nullable(),
  });

  // ── Table columns ────────────────────────────────────────────────────
  const columns = [
    {
      field: "company_name",
      headerName: "Company Name",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "display_name",
      headerName: "Display Name",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "today_scan_count",
      headerName: "Today's Scan",
      headerClassName: "table-header",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ color: "#2563eb" }}>{params.value ?? 0}</Box>
      ),
    },
    {
      field: "scan_count",
      headerName: "Total Scan",
      headerClassName: "table-header",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ color: "#059669" }}>{params.value ?? 0}</Box>
      ),
    },
    {
      field: "contact_no",
      headerName: "Contact No",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerClassName: "table-header",
      renderCell: (params) => (
        <ActionMenu
          params={params}
          actions={[
            {
              icon: <EditIcon color="primary" />,
              tooltip: "Edit",
              onClick: handleEdit,
            },
            {
              icon: <DeleteIcon color="error" />,
              tooltip: "Delete",
              onClick: handleDelete,
            },
            {
              icon: <SettingsIcon color="secondary" />,
              tooltip: "Configuration",
              onClick: handleViewConfiguration,
            },
            {
              icon: <Warehouse color="action" />,
              tooltip: "Yards",
              onClick: handleViewYards,
            },
            {
              icon: <DownloadIcon color="success" />,
              tooltip: "Download Images",
              onClick: handleDownloadImages,
            },
          ]}
        />
      ),
      sortable: false,
      filterable: false,
      flex: 1,
    },
  ];

  // ── Fetch companies ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchCompanies = async () => {
      setLoading((prev) => ({ ...prev, fetch: true }));
      try {
        const companiesData = await getAllCompanies();
        if (companiesData.status && companiesData.data) {
          setCompanies(companiesData.data);
        } else {
          errorMessage(companiesData.message || "Failed to fetch companies.");
        }
      } catch (error) {
        errorMessage(getErrorMessage(error));
      } finally {
        setLoading((prev) => ({ ...prev, fetch: false }));
      }
    };
    fetchCompanies();
  }, []);

  // ── Re-fetch users when yard changes ────────────────────────────────
  useEffect(() => {
    if (!downloadOpen || !downloadCompany) return;
    if (noYardsSelected) {
      setUsers([]);
      setSelectedUserIds(null);
      return;
    }
    const fetchUsers = async () => {
      setUsers([]);
      setSelectedUserIds(null);
      const yardIdsToFetch =
        selectedYardIds === null
          ? yards.map((y) => y.yard_id)
          : selectedYardIds;
      if (yardIdsToFetch.length === 0) return;
      setLoading((prev) => ({ ...prev, users: true }));
      try {
        const results = await Promise.all(
          yardIdsToFetch.map((id) => getUsers(downloadCompany.company_id, id)),
        );
        const merged = results
          .flatMap((r) => (r.status && r.data ? r.data : []))
          .reduce((acc, user) => {
            if (!acc.find((u) => u.user_id === user.user_id)) acc.push(user);
            return acc;
          }, []);
        setUsers(merged);
      } catch (error) {
        errorMessage(getErrorMessage(error));
      } finally {
        setLoading((prev) => ({ ...prev, users: false }));
      }
    };
    fetchUsers();
  }, [selectedYardIds, downloadOpen]);

  const handleViewConfiguration = (params) => {
    navigate(`/company/${params.row.company_id}/configuration`, {
      state: { company_name: params.row.company_name },
    });
  };

  const handleViewYards = (params) => {
    navigate(`/company/${params.row.company_id}/yards`, {
      state: { company_name: params.row.company_name },
    });
  };

  const handleDownloadImages = async (params) => {
    setDownloadCompany(params.row);
    setDownloadDates({ startDate: TODAY, endDate: TODAY });
    setDownloadDateErrors({});
    setSelectedYardIds(null);
    setSelectedUserIds(null);
    setUsers([]);
    setLoading((prev) => ({ ...prev, yards: true }));
    try {
      const yardsData = await getYardsByCompanyId(params.row.company_id);
      if (yardsData.status && yardsData.data) {
        setYards(yardsData.data);
      } else {
        errorMessage(yardsData.message || "Failed to fetch yards.");
      }
    } catch (error) {
      errorMessage(getErrorMessage(error));
    } finally {
      setLoading((prev) => ({ ...prev, yards: false }));
    }
    setDownloadOpen(true);
  };

  const handleDownloadClose = () => {
    setDownloadOpen(false);
    setDownloadCompany(null);
    setDownloadDates({ startDate: TODAY, endDate: TODAY });
    setDownloadDateErrors({});
    setYards([]);
    setUsers([]);
    setSelectedYardIds(null);
    setSelectedUserIds(null);
  };

  const handleYardToggle = (yardId) => {
    if (yardId === "all") {
      setSelectedYardIds(allYardsSelected ? [] : null);
      return;
    }
    if (allYardsSelected) {
      setSelectedYardIds(
        yards.filter((y) => y.yard_id !== yardId).map((y) => y.yard_id),
      );
    } else {
      const already = selectedYardIds.includes(yardId);
      if (already) {
        setSelectedYardIds(selectedYardIds.filter((id) => id !== yardId));
      } else {
        const next = [...selectedYardIds, yardId];
        setSelectedYardIds(next.length === yards.length ? null : next);
      }
    }
  };

  const handleUserToggle = (userId) => {
    const allUsersSelected = selectedUserIds === null;
    if (userId === "all") {
      setSelectedUserIds(allUsersSelected ? [] : null);
      return;
    }
    if (allUsersSelected) {
      setSelectedUserIds(
        users.filter((u) => u.user_id !== userId).map((u) => u.user_id),
      );
    } else {
      const already = selectedUserIds.includes(userId);
      if (already) {
        setSelectedUserIds(selectedUserIds.filter((id) => id !== userId));
      } else {
        const next = [...selectedUserIds, userId];
        setSelectedUserIds(next.length === users.length ? null : next);
      }
    }
  };

  const isYardChecked = (yardId) =>
    allYardsSelected || (selectedYardIds?.includes(yardId) ?? false);
  const isUserChecked = (userId) =>
    selectedUserIds === null || (selectedUserIds?.includes(userId) ?? false);

  const yardRenderValue = () => {
    if (allYardsSelected) return <Chip label="All Yards" size="small" />;
    if (noYardsSelected)
      return <Chip label="None" size="small" color="default" />;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {selectedYardIds.map((id) => (
          <Chip
            key={id}
            label={yards.find((y) => y.yard_id === id)?.yard_name ?? id}
            size="small"
          />
        ))}
      </Box>
    );
  };

  const userRenderValue = () => {
    if (selectedUserIds === null)
      return <Chip label="All Users" size="small" />;
    if (selectedUserIds.length === 0)
      return <Chip label="None" size="small" color="default" />;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {selectedUserIds.map((id) => (
          <Chip
            key={id}
            label={users.find((u) => u.user_id === id)?.user_name ?? id}
            size="small"
          />
        ))}
      </Box>
    );
  };

  const validateDownloadDates = () => {
    const errors = {};
    if (!downloadDates.startDate) errors.startDate = "Start date is required";
    if (!downloadDates.endDate) errors.endDate = "End date is required";
    if (
      downloadDates.startDate &&
      downloadDates.endDate &&
      downloadDates.startDate > downloadDates.endDate
    ) {
      errors.endDate = "End date must be after start date";
    }
    return errors;
  };

  const handleDownloadConfirm = async () => {
    const errors = validateDownloadDates();
    if (Object.keys(errors).length > 0) {
      setDownloadDateErrors(errors);
      return;
    }
    const yard_ids =
      selectedYardIds === null ? yards.map((y) => y.yard_id) : selectedYardIds;
    const user_ids =
      selectedUserIds === null ? users.map((u) => u.user_id) : selectedUserIds;
    setLoading((prev) => ({ ...prev, download: true }));
    try {
      const response = await downloadCompanyScanImages({
        companyId: downloadCompany.company_id,
        startDate: `${downloadDates.startDate}T00:00:00`,
        endDate: `${downloadDates.endDate}T23:59:59`,
        yard_ids,
        user_ids,
      });
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${downloadCompany.company_name}_images_${downloadDates.startDate}_to_${downloadDates.endDate}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      successMessage("Images downloaded successfully.");
      handleDownloadClose();
    } catch (error) {
      errorMessage(getErrorMessage(error));
    } finally {
      setLoading((prev) => ({ ...prev, download: false }));
    }
  };

  const openDialog = () => {
    setDialogOpen(true);
  };

  const closeDialog = (resetForm) => {
    resetForm();
    setEditCompany(null);
    setDialogOpen(false);
  };
  const handleEdit = (params) => {
    setEditCompany(params.row);
    openDialog();
  };
  const handleDelete = (params) => {
    setItemToDelete(params);
    setDeleteOpen(true);
  };
  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setItemToDelete(null);
  };
  const confirmDialog = async (values, { resetForm }) => {
    const success = editCompany
      ? await handleUpdate(values)
      : await handleCreate(values);
    if (success) closeDialog(resetForm);
  };

  const handleCreate = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    try {
      const payload = {
        company_name: values.companyName.trim(),
        display_name: values.displayName.trim(),
        company_logo: values.companyLogo?.trim() || null,
        gst_number: values.gstNumber?.trim() || null,
        address: values.address?.trim() || null,
        contact_no: values.contactNo?.trim() || null,
      };
      const response = await createCompany(payload);
      if (response.status) {
        setCompanies((prev) => [
          ...prev,
          { ...payload, company_id: response.data.company_id },
        ]);
        successMessage(response.message);
        return true;
      }
      errorMessage(response.message);
      return false;
    } catch (error) {
      errorMessage(getErrorMessage(error));
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const handleUpdate = async (values) => {
    setLoading((prev) => ({ ...prev, update: true }));
    try {
      const payload = {
        company_id: editCompany.company_id,
        company_name: values.companyName.trim(),
        display_name: values.displayName.trim(),
        company_logo: values.companyLogo?.trim(),
        gst_number: values.gstNumber?.trim(),
        address: values.address?.trim(),
        contact_no: values.contactNo?.trim(),
      };
      const response = await updateCompany(payload);
      if (response.status) {
        setCompanies((prev) =>
          prev.map((c) =>
            c.company_id === payload.company_id ? { ...c, ...payload } : c,
          ),
        );
        successMessage(response.message);
        return true;
      }
      errorMessage(response.message);
      return false;
    } catch (error) {
      errorMessage(getErrorMessage(error));
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading((prev) => ({ ...prev, delete: true }));
    try {
      const response = await deleteCompany(itemToDelete.row.company_id);
      if (response.status) {
        setCompanies((prev) =>
          prev.filter((c) => c.company_id !== itemToDelete.row.company_id),
        );
        successMessage(response.message);
      } else {
        errorMessage(response.message);
      }
    } catch (error) {
      errorMessage(getErrorMessage(error));
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
      setDeleteOpen(false);
    }
  };

  return (
    <>
      {loading.fetch ? (
        <Loader color="primary" size={40} />
      ) : (
        <Box>
          <main className="m-a-50">
            <div>
              <div className="d-flex justify-end">
                <div className="d-flex justify-between w-100">
                  <TextField
                    className="w-25"
                    label="Search"
                    variant="outlined"
                    value={searchQuery}
                    size="small"
                    startIcon={<SearchIcon />}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <Button
                    label="Add"
                    variant="contained"
                    color="primary"
                    onClick={openDialog}
                  />
                </div>
              </div>
            </div>
            <div
              style={{ height: "100%", width: "100%" }}
              className="ag-theme-alpine m-t-30"
            >
              <GridTable
                rows={companies}
                columns={columns}
                pageSize={10}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                getRowId={(row) => row.company_id}
                density="standard"
              />
            </div>
          </main>
        </Box>
      )}

      {/* ── Download Images Dialog ─────────────────────────────────────── */}
      <Dialog
        open={downloadOpen}
        onClose={handleDownloadClose}
        onConfirm={handleDownloadConfirm}
        title={!loading.download ? "Download Scan Images" : ""}
        confirmButtonText={!loading.download ? "Download" : ""}
        cancelButtonText={!loading.download ? "Cancel" : ""}
        icon={<DownloadIcon />}
        message={
          loading.download ? (
            <Box sx={{ p: 5, display: "flex", justifyContent: "center" }}>
              <Loader color="primary" size={40} />
            </Box>
          ) : (
            <Box sx={{ p: "28px 32px 24px" }}>
              {downloadCompany && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 3,
                    p: "10px 14px",
                    background: "#F0F7FF",
                    borderRadius: 2,
                    border: "1px solid #BFDBFE",
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 18, color: "#2563EB" }} />
                  <Typography
                    variant="body2"
                    sx={{ color: "#1E40AF", fontWeight: 500 }}
                  >
                    {downloadCompany.company_name}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mb: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Box>
                      <Typography
                        sx={{ ...fieldLabelStyle, fontSize: 10, mb: "4px" }}
                      >
                        Start date <span style={{ color: "#E24B4A" }}>*</span>
                      </Typography>
                      <input
                        type="date"
                        value={downloadDates.startDate}
                        max={downloadDates.endDate || undefined}
                        onChange={(e) => {
                          setDownloadDates((p) => ({
                            ...p,
                            startDate: e.target.value,
                          }));
                          setDownloadDateErrors((p) => ({
                            ...p,
                            startDate: "",
                          }));
                        }}
                        style={{
                          ...inputBaseStyle,
                          ...(downloadDateErrors.startDate
                            ? inputErrorStyle
                            : {}),
                        }}
                      />
                      {downloadDateErrors.startDate && (
                        <Typography
                          sx={{ fontSize: 11, color: "#E24B4A", mt: "4px" }}
                        >
                          {downloadDateErrors.startDate}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <Box>
                      <Typography
                        sx={{ ...fieldLabelStyle, fontSize: 10, mb: "4px" }}
                      >
                        End date <span style={{ color: "#E24B4A" }}>*</span>
                      </Typography>
                      <input
                        type="date"
                        value={downloadDates.endDate}
                        min={downloadDates.startDate || undefined}
                        onChange={(e) => {
                          setDownloadDates((p) => ({
                            ...p,
                            endDate: e.target.value,
                          }));
                          setDownloadDateErrors((p) => ({ ...p, endDate: "" }));
                        }}
                        style={{
                          ...inputBaseStyle,
                          ...(downloadDateErrors.endDate
                            ? inputErrorStyle
                            : {}),
                        }}
                      />
                      {downloadDateErrors.endDate && (
                        <Typography
                          sx={{ fontSize: 11, color: "#E24B4A", mt: "4px" }}
                        >
                          {downloadDateErrors.endDate}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <Divider sx={{ my: 2.5, borderColor: "#F3F4F6" }} />
              <Box>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Yards</InputLabel>
                      <Select
                        multiple
                        value={
                          allYardsSelected
                            ? yards.map((y) => y.yard_id)
                            : selectedYardIds
                        }
                        renderValue={yardRenderValue}
                        input={<OutlinedInput label="Yards" />}
                        disabled={loading.yards}
                        onChange={() => {}}
                        sx={{ borderRadius: 2, background: "#F9FAFB" }}
                      >
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleYardToggle("all");
                          }}
                        >
                          <Checkbox checked={allYardsSelected} size="small" />
                          <ListItemText
                            primary="All Yards"
                            primaryTypographyProps={{ fontSize: 14 }}
                          />
                        </MenuItem>
                        {loading.yards ? (
                          <MenuItem disabled>
                            <ListItemText primary="Loading..." />
                          </MenuItem>
                        ) : (
                          yards.map((yard) => (
                            <MenuItem
                              key={yard.yard_id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleYardToggle(yard.yard_id);
                              }}
                            >
                              <Checkbox
                                checked={isYardChecked(yard.yard_id)}
                                size="small"
                              />
                              <ListItemText
                                primary={yard.yard_name}
                                primaryTypographyProps={{ fontSize: 14 }}
                              />
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  {showUserDropdown && (
                    <Grid size={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Users</InputLabel>
                        <Select
                          multiple
                          value={
                            selectedUserIds === null
                              ? users.map((u) => u.user_id)
                              : selectedUserIds
                          }
                          renderValue={userRenderValue}
                          input={<OutlinedInput label="Users" />}
                          disabled={loading.users}
                          onChange={() => {}}
                          sx={{ borderRadius: 2, background: "#F9FAFB" }}
                        >
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUserToggle("all");
                            }}
                          >
                            <Checkbox
                              checked={selectedUserIds === null}
                              size="small"
                            />
                            <ListItemText
                              primary="All Users"
                              primaryTypographyProps={{ fontSize: 14 }}
                            />
                          </MenuItem>
                          {loading.users ? (
                            <MenuItem disabled>
                              <ListItemText primary="Loading..." />
                            </MenuItem>
                          ) : users.length === 0 ? (
                            <MenuItem disabled>
                              <ListItemText primary="No users found" />
                            </MenuItem>
                          ) : (
                            users.map((user) => (
                              <MenuItem
                                key={user.user_id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUserToggle(user.user_id);
                                }}
                              >
                                <Checkbox
                                  checked={isUserChecked(user.user_id)}
                                  size="small"
                                />
                                <ListItemText
                                  primary={user.user_name}
                                  primaryTypographyProps={{ fontSize: 14 }}
                                />
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          )
        }
      />

      {/* ── Add / Edit Company Dialog ──────────────────────────────────── */}
      <Formik
        initialValues={{
          companyName: editCompany ? editCompany.company_name : "",
          companyLogo: editCompany ? editCompany.company_logo : null,
          displayName: editCompany ? editCompany.display_name : "",
          gstNumber: editCompany ? editCompany.gst_number : null,
          contactNo: editCompany ? editCompany.contact_no : null,
          address: editCompany ? editCompany.address : null,
        }}
        validationSchema={validationSchema}
        onSubmit={confirmDialog}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          resetForm,
        }) => (
          <>
            <Dialog
              open={isDialogOpen}
              onClose={() => closeDialog(resetForm)}
              onConfirm={handleSubmit}
              title={
                !loading.update && !loading.create
                  ? editCompany
                    ? "Edit Company"
                    : "Add New Company"
                  : ""
              }
              confirmButtonText={
                !loading.update && !loading.create
                  ? editCompany
                    ? "Update"
                    : "Add"
                  : ""
              }
              cancelButtonText={
                !loading.update && !loading.create ? "Cancel" : ""
              }
              icon={<BusinessIcon />}
              message={
                <Box padding={4}>
                  {loading.update || loading.create ? (
                    <Loader color="primary" size={40} />
                  ) : (
                    <Form>
                      <Grid container spacing={2}>
                        <Grid size={4}>
                          <TextField
                            id="companyName"
                            name="companyName"
                            label="Company Name"
                            required
                            value={values.companyName}
                            onChange={handleChange}
                            error={
                              touched.companyName && Boolean(errors.companyName)
                            }
                            helperText={
                              touched.companyName && errors.companyName
                            }
                          />
                        </Grid>
                        <Grid size={4}>
                          <TextField
                            id="displayName"
                            name="displayName"
                            label="Display Name"
                            required
                            value={values.displayName}
                            onChange={handleChange}
                            error={
                              touched.displayName && Boolean(errors.displayName)
                            }
                            helperText={
                              touched.displayName && errors.displayName
                            }
                          />
                        </Grid>
                        <Grid size={4}>
                          <TextField
                            id="companyLogo"
                            name="companyLogo"
                            label="Company Logo"
                            value={values.companyLogo ?? ""}
                            onChange={handleChange}
                            error={
                              touched.companyLogo && Boolean(errors.companyLogo)
                            }
                            helperText={
                              touched.companyLogo && errors.companyLogo
                            }
                          />
                        </Grid>
                        <Grid size={4}>
                          <TextField
                            id="gstNumber"
                            name="gstNumber"
                            label="GST Number"
                            value={values.gstNumber ?? ""}
                            onChange={handleChange}
                            error={
                              touched.gstNumber && Boolean(errors.gstNumber)
                            }
                            helperText={touched.gstNumber && errors.gstNumber}
                          />
                        </Grid>
                        <Grid size={4}>
                          <TextField
                            id="contactNo"
                            name="contactNo"
                            label="Contact Number"
                            value={values.contactNo ?? ""}
                            onChange={handleChange}
                            error={
                              touched.contactNo && Boolean(errors.contactNo)
                            }
                            helperText={touched.contactNo && errors.contactNo}
                          />
                        </Grid>
                        <Grid size={4}>
                          <TextField
                            id="address"
                            name="address"
                            label="Address"
                            value={values.address ?? ""}
                            onChange={handleChange}
                            error={touched.address && Boolean(errors.address)}
                            helperText={touched.address && errors.address}
                          />
                        </Grid>
                      </Grid>
                    </Form>
                  )}
                </Box>
              }
            />
          </>
        )}
      </Formik>

      {/* ── Delete Confirmation Dialog ─────────────────────────────────── */}
      <Dialog
        open={deleteOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={!loading.delete ? "Delete Company" : ""}
        confirmButtonText={!loading.delete ? "Confirm" : ""}
        cancelButtonText={!loading.delete ? "Cancel" : ""}
        icon={<DeleteIcon />}
        message={
          <Box padding={2}>
            {loading.delete ? (
              <Loader color="primary" size={40} />
            ) : (
              "Are you sure you want to delete this company?"
            )}
          </Box>
        }
      />
    </>
  );
};

export default Company;